
import {Field, layer_worker} from './Field.js';

class FieldContourFill extends Field {
    constructor(field, args) {
        super(field, args);

        this.cmap_image = document.createElement('canvas');
        this.cmap_image.width = this.cmap.length;
        this.cmap_image.height = 1;

        let ctx = this.cmap_image.getContext('2d');

        this.cmap.getMap().forEach((stop, istop) => {
            ctx.fillStyle = stop['color'] + Math.round(stop['opacity'] * 255).toString(16);
            ctx.fillRect(istop, 0, 1, 1);
        });

        const levels = this.cmap.getLevels();
        const n_lev = levels.length - 1;

        // Build a texture to account for nonlinear colormaps (basically inverts the relationship between
        //  the normalized index and the normalized level)
        const input_norm = levels.map((lev, ilev) => ilev / n_lev);
        const cmap_norm = levels.map(lev => (lev - levels[0]) / (levels[n_lev] - levels[0]));
        const inv_cmap_norm = input_norm.map(lev => {
            let jlev;
            for (jlev = 0; !(cmap_norm[jlev] <= lev && lev <= cmap_norm[jlev + 1]); jlev++) {}

            const alpha = (lev - cmap_norm[jlev]) / (cmap_norm[jlev + 1] - cmap_norm[jlev]);
            return input_norm[jlev] * (1 - alpha) + input_norm[jlev + 1] * alpha;
        });

        this.index_map = new Float32Array(inv_cmap_norm);
    }

    async onAdd(map, gl) {
        // Basic procedure for the filled contours inspired by https://blog.mbq.me/webgl-weather-globe/
        gl.getExtension('OES_texture_float');
        gl.getExtension('OES_texture_float_linear');

        const vertexSource = `
        uniform mat4 u_matrix;

        attribute vec2 a_pos;
        attribute vec2 a_tex_coord;

        varying highp vec2 v_tex_coord;

        void main() {
            gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0);
            v_tex_coord = a_tex_coord;
        }`;
        
        // create GLSL source for fragment shader
        const fragmentSource = `
        varying highp vec2 v_tex_coord;

        uniform sampler2D u_fill_sampler;
        uniform sampler2D u_cmap_sampler;
        uniform sampler2D u_cmap_nonlin_sampler;
        uniform highp float u_cmap_min;
        uniform highp float u_cmap_max;

        void main() {
            highp vec4 fill_val = texture2D(u_fill_sampler, v_tex_coord);
            lowp float normed_val = (fill_val.r - u_cmap_min) / (u_cmap_max - u_cmap_min);
            
            if (normed_val <= 0.0) {
                discard;
            }

            highp vec4 nonlin_val = texture2D(u_cmap_nonlin_sampler, vec2(clamp(normed_val, 0.0, 1.0), 0.5));
            lowp vec4 color = texture2D(u_cmap_sampler, vec2(nonlin_val.r, 0.5));
            color.a = color.a * 0.6;
            gl_FragColor = color;
        }`;
        
        this.program = this._compileAndLinkShaders(gl, vertexSource, fragmentSource);

        const verts_tex_coords = await layer_worker.makeDomainVerticesAndTexCoords(this.field);

        this.vertices = this._setupVertices(gl, this.program, verts_tex_coords['vertices'], 2, 'a_pos');

        const fill_image = {'format': gl.LUMINANCE, 'type': gl.FLOAT, 
            'width': this.field['tex_width'], 'height': this.field['tex_height'],
            'image': this.field['data'],
            'mag_filter': gl.LINEAR,
        };

        this.fill_texture = this._setupTexture(gl, this.program, fill_image, 'u_fill_sampler', 
            verts_tex_coords['tex_coords'], 'a_tex_coord');

        const cmap_image = {'format': gl.RGBA, 'type': gl.UNSIGNED_BYTE, 'image': this.cmap_image, 'mag_filter': gl.NEAREST};
        this.cmap_texture = this._setupTexture(gl, this.program, cmap_image, 'u_cmap_sampler');

        const cmap_nonlin_image = {'format': gl.LUMINANCE, 'type': gl.FLOAT, 
            'width': this.index_map.length, 'height': 1,
            'image': this.index_map, 
            'mag_filter': gl.LINEAR};
        this.cmap_nonlin_texture = this._setupTexture(gl, this.program, cmap_nonlin_image, 'u_cmap_nonlin_sampler');

        this.u_cmin_pos = gl.getUniformLocation(this.program, 'u_cmap_min');
        this.u_cmax_pos = gl.getUniformLocation(this.program, 'u_cmap_max');
        this.is_initialized = true;
    }

    render(gl, matrix) {
        if (!this.is_initialized) return;

        gl.useProgram(this.program);
        gl.uniformMatrix4fv(
            gl.getUniformLocation(this.program, 'u_matrix'),
            false,
            matrix
        );

        gl.uniform1f(this.u_cmin_pos, this.cmap.getMap()[0]['level']);
        gl.uniform1f(this.u_cmax_pos, this.cmap.getMap()[this.cmap.length - 1]['level']);

        this._bindTexture(gl, 0, this.fill_texture);
        this._bindTexture(gl, 1, this.cmap_texture);
        this._bindTexture(gl, 2, this.cmap_nonlin_texture);

        this._bindVertices(gl, this.vertices);

        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, this.field['nlats'] * 2);
    }
}

export default FieldContourFill;